import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, Search, Copy, CheckCircle, Info, Upload } from "../../utils/icons";
import { storage } from "../../utils/storage";
import { showSuccess, showError } from "../../utils/toast";
import coins from "../../utils/coins";

// ─── Constants ────────────────────────────────────────────────────────────────
const BASE = "https://api.air1bet.com";

const FIAT_CURRENCIES = ["INR", "BDT", "MVR", "PKR", "AED"];

const isFiat = (c) => FIAT_CURRENCIES.includes(c?.toUpperCase());

const tokens = [
  { id: "eth", name: "ETH", icon: "/assets/images/ETH.png" },
  { id: "btc", name: "BTC", icon: "/assets/images/BTC.png" },
  { id: "usdt", name: "USDT", icon: "/assets/images/TETHER.png" },
  { id: "doge", name: "DOGE", icon: "/assets/images/DOGE.png" },
];

const getAuthToken = () => {
  let t = storage.getKey("token") || localStorage.getItem("token");
  if (!t) return null;
  // Remove wrapping quotes if present (e.g. "\"eyJhb...\"")
  try { t = JSON.parse(t); } catch {}
  if (typeof t === "string" && t.startsWith('"') && t.endsWith('"')) {
    t = t.slice(1, -1);
  }
  return t;
};
const getUID = () => {
  let u = localStorage.getItem("uid") || storage.getKey("uid");
  if (!u) return null;
  try { u = JSON.parse(u); } catch {}
  return u?.toString();
};

// ─── QR image from Buffer helper ──────────────────────────────────────────────
const QRImageComponent = ({ qrImage, alt = "QR Code", style }) => {
  const [imgSrc, setImgSrc] = useState("");
  useEffect(() => {
    if (qrImage?.data && Array.isArray(qrImage.data)) {
      try {
        const base64 = btoa(String.fromCharCode(...new Uint8Array(qrImage.data)));
        setImgSrc(`data:image/png;base64,${base64}`);
      } catch (e) {
        setImgSrc("");
      }
    } else if (typeof qrImage === "string") {
      setImgSrc(qrImage);
    }
  }, [qrImage]);
  if (!imgSrc) return <div style={{ color: "#aaa", fontSize: 12, textAlign: "center" }}>Loading QR...</div>;
  return <img src={imgSrc} alt={alt} style={{ width: "100%", height: "100%", objectFit: "contain", ...(style || {}) }} />;
};

// ─── Inline style helpers ─────────────────────────────────────────────────────
const S = {
  input: {
    width: "100%",
    padding: "8px 12px",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    color: "#fff",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },
  btn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "9px 14px",
    background: "var(--color-accent-coral)",
    color: "#000",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    width: "100%",
  },
  label: { color: "#B3BEC1", fontSize: 13, marginBottom: 4 },
  dropBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 12px",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
  },
  dropPanel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "var(--color-surface-dark)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    maxHeight: 320,
    overflowY: "auto",
    zIndex: 1100,
    padding: "8px 10px",
  },
  coinOption: (selected) => ({
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 10,
    background: selected ? "rgba(255,92,92,0.1)" : "transparent",
    gap: 10,
  }),
  addressBox: {
    background: "rgba(0,0,0,0.3)",
    padding: "10px 12px",
    borderRadius: 6,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  copyBtn: {
    background: "rgba(255,255,255,0.08)",
    border: "none",
    color: "#fff",
    borderRadius: 4,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    fontSize: 12,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  noteBox: (color) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: "rgba(0,0,0,0.25)",
    borderRadius: 8,
    padding: "10px 14px",
    borderLeft: `3px solid ${color}`,
    marginTop: 10,
  }),
  bonusCard: (selected) => ({
    background: "linear-gradient(180deg, #1b2326 0%, #182024 100%)",
    border: `1px solid ${selected ? "var(--color-accent-coral)" : "transparent"}`,
    borderRadius: 14,
    padding: 16,
    marginBottom: 10,
    width: "100%",
    boxSizing: "border-box",
  }),
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24, gap: 12 }}>
    <div style={{
      width: 28, height: 28,
      border: "3px solid rgba(255,92,92,0.2)",
      borderTopColor: "var(--color-accent-coral)",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    }} />
    <span style={{ color: "#aaa", fontSize: 14 }}>Loading…</span>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ─── Processing overlay ───────────────────────────────────────────────────────
const ProcessingOverlay = () => (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(3,3,15,0.95)",
    zIndex: 9999,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 20,
  }}>
    <div style={{
      width: 52, height: 52,
      border: "4px solid rgba(255,92,92,0.2)",
      borderTopColor: "var(--color-accent-coral)",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    }} />
    <div style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>Processing Your Deposit</div>
    <div style={{ color: "var(--color-text-muted)", fontSize: 14, textAlign: "center", maxWidth: 300 }}>
      Please wait while we redirect you to the payment gateway. Do not close or refresh this page.
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const DepositModal = ({ onClose }) => {
  // ── Coin / dropdown state ────────────────────────────────────────────────────
  const [selectedCoin, setSelectedCoin] = useState(storage.getKey("coin") || "INR");
  const [showCoinDrop, setShowCoinDrop] = useState(false);
  const [coinSearch, setCoinSearch] = useState("");

  // ── Network dropdown (crypto) ────────────────────────────────────────────────
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [showNetworkDrop, setShowNetworkDrop] = useState(false);

  // ── Bank dropdown (fiat manual) ──────────────────────────────────────────────
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null); // full bank object
  const [showBankDrop, setShowBankDrop] = useState(false);

  // ── Amount & error ───────────────────────────────────────────────────────────
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  // ── Payment method (INR specific) ────────────────────────────────────────────
  const [upiMethod, setUpiMethod] = useState("f2pay"); // 'f2pay' | 'upi2'

  // ── User info ────────────────────────────────────────────────────────────────
  const [customerName, setCustomerName] = useState(
    storage.getKey("name") || localStorage.getItem("name") || ""
  );
  const [customerEmail, setCustomerEmail] = useState(
    storage.getKey("email") || localStorage.getItem("email") || ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    storage.getKey("phone") || localStorage.getItem("phone") ||
    storage.getKey("mobile") || localStorage.getItem("mobile") || ""
  );

  // ── Crypto deposit details ────────────────────────────────────────────────────
  const [coinId, setCoinId] = useState("");
  const [depositAddress, setDepositAddress] = useState("");
  const [qrString, setQrString] = useState("");
  const [showDepositDetails, setShowDepositDetails] = useState(false); // crypto address revealed
  const [ccInitiate, setCCInitiate] = useState(false); // fiat bank details revealed

  // ── Loading states ───────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // full-screen overlay

  // ── Manual fiat form ─────────────────────────────────────────────────────────
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [screenshot, setScreenshot] = useState(null);

  // ── Copy states ───────────────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);
  const [copiedFields, setCopiedFields] = useState({});

  // ── Bonus ─────────────────────────────────────────────────────────────────────
  const [bonusConfig, setBonusConfig] = useState(null);
  const [showBonusCards, setShowBonusCards] = useState(false);

  // ── Click-outside refs ────────────────────────────────────────────────────────
  const coinDropRef = useRef(null);
  const networkDropRef = useRef(null);
  const bankDropRef = useRef(null);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const filteredCoins = coins
    .filter((c) => c.preffix !== "SC" && c.preffix !== "BJT")
    .filter((c) => c.preffix.toLowerCase().includes(coinSearch.toLowerCase()))
    .sort((a, b) => (a.preffix === "BTC" ? -1 : b.preffix === "BTC" ? 1 : 0));

  const selectedCoinData = coins.find((c) => c.preffix === selectedCoin);
  const placeholder = selectedCoin === "MVR" ? "Enter Deposit Amount (Minimum 100 MVR)" : "Enter Deposit Amount";

  const hasActiveBonus = !!(bonusConfig?.is_active && Number(bonusConfig?.bonus_percentage || 0) > 0);
  const bonusModeLabel =
    bonusConfig?.award_mode === "every_7_days"
      ? `One bonus every ${bonusConfig?.cooldown_days || 7} days`
      : "Every qualifying deposit";

  // ─────────────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────────────

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (coinDropRef.current && !coinDropRef.current.contains(e.target)) setShowCoinDrop(false);
      if (networkDropRef.current && !networkDropRef.current.contains(e.target)) setShowNetworkDrop(false);
      if (bankDropRef.current && !bankDropRef.current.contains(e.target)) setShowBankDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // On coin change: reset deposit details & load coin info
  useEffect(() => {
    setError("");
    setShowTransactionForm(false);
    setTransactionId("");
    setScreenshot(null);
    setDepositAddress("");
    setQrString("");
    setShowDepositDetails(false);
    setCCInitiate(false);
    setNetworks([]);
    setSelectedNetwork("");
    setBankDetails([]);
    setSelectedBank(null);

    if (!isFiat(selectedCoin)) {
      loadCoinDetails(selectedCoin);
    } else if (isFiat(selectedCoin) && selectedCoin !== "INR") {
      fetchBankDetails(selectedCoin);
    }
  }, [selectedCoin]); // eslint-disable-line

  // Fetch bonus config on mount
  useEffect(() => {
    const token = getAuthToken();
    if (token) fetchBonusConfig();
    else setBonusConfig([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // API calls
  // ─────────────────────────────────────────────────────────────────────────────

  const loadCoinDetails = async (coin) => {
    const token = getAuthToken();
    if (!token) { console.warn("No token, skipping getCoinDetails"); return; }
    setIsLoading(true);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(`${BASE}/getCoinDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ symbol: coin.toUpperCase() }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) { setIsLoading(false); return; }
      const data = await res.json();
      const netObj = data?.networks || {};
      const netList = Object.keys(netObj);
      setCoinId(data?.coinId || "");
      setNetworks(netList);
      setSelectedNetwork(netList[0] || "");
    } catch (e) {
      if (e.name === "AbortError") {
        showError("Request timed out. Please try again.");
      } else {
        console.error("loadCoinDetails error:", e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBankDetails = async (coin) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${BASE}/bankDetails/${coin}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) return;
      const data = await res.json();
      setBankDetails(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchBankDetails error:", e);
    }
  };

  const fetchBonusConfig = async () => {
    const token = getAuthToken();
    if (!token) { setBonusConfig([]); return; }
    try {
      const res = await fetch(`${BASE}/api/deposit-bonus/config-public`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.status === 401) { setBonusConfig([]); return; }
      const data = await res.json();
      setBonusConfig(data);
    } catch (e) {
      setBonusConfig([]);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Payment handlers
  // ─────────────────────────────────────────────────────────────────────────────

  // Method 1 (F2Pay) – INR
  const handleF2PayPayment = async () => {
    const amt = parseFloat(amount);
    if (!amount || amt < 500) { showError("Please enter an amount of at least ₹500."); return; }
    if (!phoneNumber || phoneNumber.length < 10) { showError("Please enter a valid phone number."); return; }
    if (!customerName.trim()) { showError("Please enter your name."); return; }
    if (!customerEmail.trim()) { showError("Please enter your email."); return; }

    const token = getAuthToken();
    if (!token) { showError("Please login first."); return; }

    setIsProcessing(true);
    try {
      // Save phone to DB silently
      fetch(`${BASE}/api/user/update-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: getUID(), phone: phoneNumber }),
      }).catch(() => {});

      const res = await fetch(`${BASE}/f2pay/createOrder`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount,
          customerName: customerName.trim(),
          customerPhone: phoneNumber,
          customerEmail: customerEmail.trim(),
          subject: "Deposit",
          body: "Deposit via F2Pay",
          methodCode: "UpiMixed",
          user_id: getUID(),
        }),
      });
      const data = await res.json();
      const redirectUrl = data.payUrl || data.paymentUrl;
      if (data.success && redirectUrl) {
        window.open(redirectUrl, "_blank");
        onClose && onClose();
      } else {
        showError("Payment initiation failed");
      }
    } catch (e) {
      showError(e.message || "Error initiating payment");
    } finally {
      setIsProcessing(false);
    }
  };

  // Method 2 (UPI2/Payin) – INR
  const handleUPI2Payment = async () => {
    const amt = parseFloat(amount);
    if (!amount || amt < 100) { showError("Please enter an amount of at least ₹100."); return; }

    const token = getAuthToken();
    if (!token) { showError("Please login first."); return; }

    setIsProcessing(true);
    try {
      const res = await fetch(`${BASE}/api/payments/payin/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId: getUID(),
          currency: "INR",
          out_trade_no: `TX${Date.now()}`,
          pay_type: "UPI",
          money: amount,
          attach: "",
          notify_url: `${BASE}/api/payments/payin/callback`,
          returnUrl: "https://air1bet.com",
          phone: phoneNumber,
        }),
      });
      const json = await res.json();
      const { code, data } = json;
      if ((code === 0 || code === "0") && data?.url) {
        window.open(data.url, "_blank");
        onClose && onClose();
      } else {
        showError(json?.message || json?.error || "Payment initiation failed");
      }
    } catch (e) {
      showError("Error initiating UPI payment");
    } finally {
      setIsProcessing(false);
    }
  };

  // Fiat manual (BDT, MVR etc.) – show bank details + transaction form
  const handleFiatManualProceed = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError("Please enter deposit amount"); return; }
    // For non-INR fiat: fetch bank details if needed, reveal form
    if (selectedCoin !== "INR" && bankDetails.length === 0) {
      await fetchBankDetails(selectedCoin);
    }
    setCCInitiate(true);
    setShowTransactionForm(true);
  };

  // Crypto – call createDeposit API
  const initiateCryptoDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) { showError("Please enter a valid amount."); return; }
    if (!selectedNetwork) { showError("Please choose a network."); return; }

    const token = getAuthToken();
    if (!token) { showError("Please login first."); return; }

    setIsLoading(true);
    try {
      const res = await fetch(`${BASE}/createDeposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          coinId,
          price: amount,
          orderId: `order${Date.now()}`,
          chain: selectedNetwork,
          generateCheckoutURL: true,
          returnUrl: `${BASE}/`,
          userid: getUID()?.toString(),
        }),
      });
      const json = await res.json();
      const addr = json?.data?.address;
      if (addr) {
        setDepositAddress(addr);
        setQrString(addr);
        setShowDepositDetails(true);
      } else {
        showError("Deposit initiation failed");
      }
    } catch (e) {
      showError("Error initiating deposit");
    } finally {
      setIsLoading(false);
    }
  };

  // Manual fiat submit (transaction ID + screenshot)
  const handleSubmitManualFiat = async () => {
    if (!transactionId || !screenshot) { setError("Please fill all required fields"); return; }
    if (!amount || parseFloat(amount) <= 0) { setError("Please enter a valid amount"); return; }

    const token = getAuthToken();
    const formData = new FormData();
    formData.append("user_id", getUID());
    formData.append("amount", amount);
    formData.append("transaction_id", transactionId);
    formData.append("screenshot", screenshot);
    formData.append("currency", selectedCoin);
    if (selectedBank) {
      formData.append("bank_name", selectedBank.bank_name);
      formData.append("account_number", selectedBank.account_number || "");
      formData.append("account_holder_name", selectedBank.account_holder_name || "");
    }

    try {
      const res = await fetch(`${BASE}/api/deposits/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Deposit request submitted successfully");
        setShowTransactionForm(false);
        setTransactionId("");
        setScreenshot(null);
        setCCInitiate(false);
        setAmount("");
      } else {
        setError(data.message || "Failed to submit deposit request");
      }
    } catch (e) {
      setError("Failed to submit deposit request");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Copy helper
  // ─────────────────────────────────────────────────────────────────────────────
  const copyText = (value, field = "main") => {
    navigator.clipboard.writeText(value).then(() => {
      if (field === "main") {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setCopiedFields((p) => ({ ...p, [field]: true }));
        setTimeout(() => setCopiedFields((p) => ({ ...p, [field]: false })), 2000);
      }
      showSuccess("Copied!");
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Coin selection
  // ─────────────────────────────────────────────────────────────────────────────
  const selectCoin = (preffix) => {
    storage.setKey("coin", preffix);
    setSelectedCoin(preffix);
    setShowCoinDrop(false);
    setCoinSearch("");
    setAmount("");
    setError("");
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const renderAddressField = (label, value, field) => (
    <div style={S.addressBox}>
      <span style={{ fontSize: 13, color: "#ccc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
        {label}: <strong style={{ color: "#fff" }}>{value}</strong>
      </span>
      <button style={S.copyBtn} onClick={() => copyText(value, field)}>
        {copiedFields[field] ? <CheckCircle size={14} color="var(--color-accent-green)" /> : <Copy size={14} />}
        {copiedFields[field] ? "Copied" : "Copy"}
      </button>
    </div>
  );

  const renderBankDetails = () => {
    if (!selectedBank) return null;
    return (
      <div style={{ marginTop: 12 }}>
        {selectedBank.account_holder_name && renderAddressField("Account Holder", selectedBank.account_holder_name, "accountName")}
        {selectedBank.account_number && renderAddressField("Account Number", selectedBank.account_number, "accountNumber")}
        {selectedBank.ifsc_code && renderAddressField("IFSC Code", selectedBank.ifsc_code, "ifscCode")}
        {selectedBank.upi_id && renderAddressField("UPI ID", selectedBank.upi_id, "upiId")}
      </div>
    );
  };

  const renderINRMethods = () => (
    <div style={{ marginTop: 12 }}>
      {/* Radio: Payment Method 1 / 2 */}
      <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
        {[
          { value: "f2pay", label: "Payment Method 1" },
          { value: "upi2", label: "Payment Method 2" },
        ].map(({ value, label }) => (
          <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "#fff", fontSize: 14 }}>
            <input
              type="radio"
              name="inrMethod"
              checked={upiMethod === value}
              onChange={() => setUpiMethod(value)}
              style={{ accentColor: "var(--color-accent-coral)", width: 16, height: 16 }}
            />
            {label}
          </label>
        ))}
      </div>

      {/* Method 1 (F2Pay) – name + phone + email */}
      {upiMethod === "f2pay" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <input
            style={S.input}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Enter Your Name"
            autoComplete="name"
          />
          <input
            style={S.input}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Enter Phone Number"
            autoComplete="tel"
          />
          <input
            style={S.input}
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="Enter Email Address"
            autoComplete="email"
          />
        </div>
      )}

      {/* Method 2 (UPI2) – phone only */}
      {upiMethod === "upi2" && (
        <div style={{ marginBottom: 12 }}>
          <input
            style={S.input}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Enter Phone Number"
            autoComplete="tel"
          />
        </div>
      )}

      <button
        style={{ ...S.btn, opacity: isProcessing ? 0.7 : 1, cursor: isProcessing ? "not-allowed" : "pointer" }}
        onClick={upiMethod === "f2pay" ? handleF2PayPayment : handleUPI2Payment}
        disabled={isProcessing}
      >
        {isProcessing ? "Processing…" : "Proceed to Payment"}
      </button>
    </div>
  );

  const renderBonusCards = () => {
    if (!hasActiveBonus) {
      return <div style={{ color: "#B3BEC1", fontSize: 13, marginTop: 4 }}>Deposit bonus is currently unavailable.</div>;
    }
    return (
      <div style={{ marginTop: 12 }}>
        <div style={S.bonusCard(true)}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <img src="/assets/images/bonus/daily.png" alt="bonus" style={{ width: 28, height: 28, objectFit: "contain" }}
              onError={(e) => { e.target.style.display = "none"; }} />
            <span style={{ color: "var(--color-accent-coral)", fontWeight: 700, fontSize: 15 }}>Deposit Bonus</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <div style={{ flex: "1 1 140px" }}>
              <span style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                Get <span style={{ color: "var(--color-accent-coral)", fontWeight: 800 }}>{bonusConfig?.bonus_percentage || 0}%</span> extra on eligible deposits.
              </span>
            </div>
            <div>
              <div style={{ color: "#93a0a4", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>Min Deposit</div>
              <div style={{ color: "#eef2f3", fontWeight: 600, fontSize: 14 }}>{bonusConfig?.min_deposit_amount || 0}</div>
            </div>
            <div>
              <div style={{ color: "#93a0a4", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>Mode</div>
              <div style={{ color: "#eef2f3", fontWeight: 600, fontSize: 14 }}>{bonusModeLabel}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "4px 0", color: "#fff" }}>
      {isProcessing && <ProcessingOverlay />}

      {/* ── Quick token buttons ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {tokens.map((t) => (
          <button
            key={t.id}
            onClick={() => selectCoin(t.name)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 14px",
              background: selectedCoin === t.name ? "rgba(255,92,92,0.15)" : "var(--color-surface-dark)",
              border: selectedCoin === t.name ? "1px solid rgba(255,92,92,0.5)" : "1px solid transparent",
              borderRadius: 100, cursor: "pointer", color: "#fff",
            }}
          >
            <img src={t.icon} alt={t.name} style={{ width: 20, height: 20, borderRadius: "50%" }}
              onError={(e) => { e.target.style.display = "none"; }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</span>
          </button>
        ))}
      </div>

      {/* ── Coin selector + Network/Bank selector row ───────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>

        {/* Coin dropdown */}
        <div style={{ flex: 1 }} ref={coinDropRef}>
          <div style={S.label}>Deposit Currency</div>
          <div style={{ position: "relative" }}>
            <button style={S.dropBtn} onClick={() => { setShowCoinDrop(!showCoinDrop); setShowNetworkDrop(false); setShowBankDrop(false); }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img
                  src={`/assets/images/${selectedCoinData?.image || selectedCoin + ".png"}`}
                  alt={selectedCoin}
                  style={{ width: 22, height: 22, borderRadius: "50%" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span style={{ fontWeight: 600 }}>{selectedCoin}</span>
              </div>
              <ChevronDown size={16} />
            </button>

            {showCoinDrop && (
              <div style={S.dropPanel}>
                {/* Search */}
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <Search size={16} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
                  <input
                    style={{ ...S.input, paddingLeft: 34 }}
                    placeholder="Search coins…"
                    value={coinSearch}
                    onChange={(e) => setCoinSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                {filteredCoins.map((c) => (
                  <div
                    key={c.preffix}
                    style={S.coinOption(selectedCoin === c.preffix)}
                    onClick={() => selectCoin(c.preffix)}
                  >
                    <img src={`/assets/images/${c.image}`} alt={c.preffix}
                      style={{ width: 22, height: 22, borderRadius: "50%" }}
                      onError={(e) => { e.target.style.display = "none"; }} />
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{c.preffix}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Network dropdown (crypto only) */}
        {!isFiat(selectedCoin) && networks.length > 0 && (
          <div style={{ flex: 1 }} ref={networkDropRef}>
            <div style={S.label}>Choose Network</div>
            <div style={{ position: "relative" }}>
              <button style={S.dropBtn} onClick={() => { setShowNetworkDrop(!showNetworkDrop); setShowCoinDrop(false); }}>
                <span style={{ fontWeight: 600 }}>{selectedNetwork || "Select Network"}</span>
                <ChevronDown size={16} />
              </button>
              {showNetworkDrop && (
                <div style={S.dropPanel}>
                  {networks.map((net) => (
                    <div
                      key={net}
                      style={S.coinOption(selectedNetwork === net)}
                      onClick={() => { setSelectedNetwork(net); setShowNetworkDrop(false); }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{net}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bank dropdown (non-INR fiat, manual mode) */}
        {isFiat(selectedCoin) && selectedCoin !== "INR" && bankDetails.length > 0 && (
          <div style={{ flex: 1 }} ref={bankDropRef}>
            <div style={S.label}>Choose Bank</div>
            <div style={{ position: "relative" }}>
              <button style={S.dropBtn} onClick={() => { setShowBankDrop(!showBankDrop); setShowCoinDrop(false); }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{selectedBank?.bank_name || "Select Bank"}</span>
                <ChevronDown size={16} />
              </button>
              {showBankDrop && (
                <div style={S.dropPanel}>
                  {bankDetails.map((bank) => (
                    <div
                      key={bank.id || bank.bank_name}
                      style={S.coinOption(selectedBank?.bank_name === bank.bank_name)}
                      onClick={() => { setSelectedBank(bank); setShowBankDrop(false); }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{bank.bank_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Get Bonus button (INR + active bonus only) ──────────────────────── */}
      {selectedCoin === "INR" && hasActiveBonus && (
        <div style={{ marginBottom: 12 }}>
          <button
            style={{ ...S.btn, width: "auto", padding: "7px 20px" }}
            onClick={() => setShowBonusCards((p) => !p)}
          >
            {showBonusCards ? "Hide Bonus" : "Get Bonus"}
          </button>
          {showBonusCards && renderBonusCards()}
        </div>
      )}

      {/* ── Loading spinner (coin details fetch) ───────────────────────────── */}
      {isLoading && <Spinner />}

      {/* ── Crypto deposit details (address + QR) ──────────────────────────── */}
      {!isLoading && showDepositDetails && !isFiat(selectedCoin) && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
          {/* QR */}
          <div style={{ width: 130, height: 130, background: "#fff", borderRadius: 6, flexShrink: 0, padding: 4, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(qrString)}`}
              alt="QR Code"
              style={{ width: 120, height: 120 }}
            />
          </div>
          {/* Address */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#9ba1a6", fontSize: 13, marginBottom: 6 }}>Deposit Address</div>
            <div style={S.addressBox}>
              <span style={{ fontSize: 12, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace", minWidth: 0 }}>
                {depositAddress}
              </span>
              <button style={S.copyBtn} onClick={() => copyText(depositAddress)}>
                {copied ? <CheckCircle size={14} color="var(--color-accent-green)" /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {selectedNetwork && (
              <div style={{ color: "#aaa", fontSize: 12 }}>Network: <strong style={{ color: "#fff" }}>{selectedNetwork}</strong></div>
            )}
          </div>
        </div>
      )}

      {/* ── Fiat manual: bank QR + bank details ────────────────────────────── */}
      {!isLoading && ccInitiate && isFiat(selectedCoin) && selectedBank && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-start" }}>
          {/* Bank QR image */}
          {selectedBank.qr_image && (
            <div style={{ width: 120, height: 120, background: "#fff", borderRadius: 6, flexShrink: 0, padding: 4, boxSizing: "border-box" }}>
              <QRImageComponent qrImage={selectedBank.qr_image} alt="Bank QR" />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {renderBankDetails()}
          </div>
        </div>
      )}

      {/* ── Amount input ────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 4 }}>
        <input
          style={S.input}
          type="number"
          value={amount}
          onChange={(e) => {
            const v = e.target.value;
            setAmount(v);
            setError("");
            if (selectedCoin === "MVR" && v !== "" && Number(v) < 100) {
              setError("Minimum deposit for MVR is 100");
            }
          }}
          placeholder={placeholder}
        />
        {error && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>

      {/* ── Payment action section ──────────────────────────────────────────── */}
      <div style={{ marginTop: 10 }}>
        {/* INR: method 1/2 selector + proceed */}
        {selectedCoin === "INR" && renderINRMethods()}

        {/* Other fiat (BDT, MVR etc.) – show bank details then manual form */}
        {isFiat(selectedCoin) && selectedCoin !== "INR" && (
          <button
            style={{ ...S.btn, marginTop: 8 }}
            onClick={handleFiatManualProceed}
          >
            Proceed to Payment
          </button>
        )}

        {/* Crypto */}
        {!isFiat(selectedCoin) && !showDepositDetails && !isLoading && (
          <button style={{ ...S.btn, marginTop: 8 }} onClick={initiateCryptoDeposit}>
            Proceed with Deposit
          </button>
        )}
      </div>

      {/* ── Manual fiat transaction form ────────────────────────────────────── */}
      {showTransactionForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
          <input
            style={S.input}
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter Transaction / UTR Number"
          />
          {/* File upload */}
          <div
            style={{
              border: "2px dashed #41ED84", borderRadius: 8, padding: 16,
              textAlign: "center", cursor: "pointer",
            }}
            onClick={() => document.getElementById("depositFileInput").click()}
          >
            <Upload size={22} color="var(--color-accent-green)" />
            <div style={{ color: "#fff", marginTop: 6, fontSize: 13 }}>
              {screenshot ? screenshot.name : "Upload Payment Screenshot"}
            </div>
            <input
              id="depositFileInput"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => { if (e.target.files[0]) setScreenshot(e.target.files[0]); }}
            />
          </div>
          <button style={S.btn} onClick={handleSubmitManualFiat}>Submit</button>
        </div>
      )}

      {/* ── Warning notes ───────────────────────────────────────────────────── */}
      <div style={{ marginTop: 14 }}>
        <div style={S.noteBox("#ab1ac6")}>
          <Info size={18} color="#ab1ac6" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#fff" }}>
            Do not deposit any currency other than {selectedCoin}
          </span>
        </div>
        <div style={S.noteBox("#FF9D02")}>
          <Info size={18} color="#FF9D02" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: "#fff", lineHeight: 1.5 }}>
            Usually deposits are instant but in some cases {selectedCoin} deposits can take up to 2 hrs.
            If you haven&apos;t received your deposit amount, please contact customer support.
          </span>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;
